import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import {
  Create,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  required,
  useGetIdentity,
  useGetList,
  useRecordContext,
  Labeled,
  FormDataConsumer,
  useNotify,
  useRefresh,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { useFormContext, useFieldArray, useWatch, Controller } from 'react-hook-form';
import {
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Tooltip,
  IconButton,
  Typography,
  Grid,
  Paper,
  Collapse,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  StepButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { TagInput } from '@/components/TagInput';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import mermaid from 'mermaid';

const queryOptions = { meta: { raw: true } };

const DOCUMENT_COMPOSER_HOW_IT_WORKS_MD = `
## Document Composer: How It Works

The Document Composer runs in **three phases**: **Analysis**, **Document creation (Apply)**, and **Validation**. Each phase uses LLM prompts configured in this ruleset (templates and rules).

### 1. Analysis

The first phase runs two skills in parallel: **Requirements** and **Redactions**.

- **Requirements**: Identifies what content is missing (e.g. required fields) and asks the user to provide or skip.
- **Redactions**: Finds passages to remove or generalize; the user then approves or rejects each suggestion.

The result is stored as "analysis" on the note. The user must resolve every item (provide/skip for requirements; approve/reject for redactions) before the next phase can run.

### 2. Document creation (Apply)

After all analysis decisions are resolved, the user can **Generate**. This runs a single LLM call that produces the output document from the original content plus the user's decisions. The template for this phase is the **Apply** template (stored as \`documentCreation\` or \`apply_changes\`).

### 3. Validation

After the document is generated, an optional **Validation** LLM call runs. It evaluates the generated document against the validation rules (e.g. meaning preservation). The result is stored and can be shown to the user (e.g. pass/fail per rule).

---

## Skills

A **skill** is a group of rules plus a fixed set of **statuses** (and optional field rules) defined in code. There are three skills:

- **Requirements** — Statuses such as requested / provided / skipped; the user provides or skips content.
- **Redactions** — Statuses such as suggested / approved / rejected / skipped; the user approves or rejects each suggested removal.
- **Validation** — Statuses such as pass / fail; the AI sets these when evaluating the generated document.

For each skill, the rules (from this ruleset) and the status/field definitions (from the skill config) are turned into **replacement tags** injected into the prompts:

- **\`@skill.<skillId>.rules\`** — List of rule ids and descriptions for that skill.
- **\`@skill.<skillId>.explainer\`** — Human-readable statuses and field rules (from Document Composer Skills when set, otherwise from the in-code definition).

These tags are available in every phase that uses that skill: Analysis uses requirements and redactions; Apply uses all three; Validation uses the validation skill. Additional runtime tags (e.g. \`@asset.grammar\`, \`@asset.userResponseContent\`, \`@asset.outputContent\`) are filled when the flow runs.
`;

const SkillPromptEditor = ({
  skillId,
  validTags,
  ruleCategories,
}: {
  skillId: string;
  validTags: string[];
  ruleCategories: RuleCategoryForm[];
}) => {
  const skillName = skillId.charAt(0).toUpperCase() + skillId.slice(1);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {skillName} Analysis Prompt
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define the AI prompts for the {skillId} skill. Use @ to insert replacement tags.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TagInput
              source={`templates.${skillId}.systemMessage`}
              label="System Message"
              validTags={validTags}
              markup="{{__id__}}"
            />
            <TagInput
              source={`templates.${skillId}.userPromptTemplate`}
              label="User Prompt Template"
              validTags={validTags}
              markup="{{__id__}}"
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                Output JSON Schema (passed to the agent directly)
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  maxHeight: 300,
                  overflow: 'auto',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontSize: '0.8rem', fontFamily: 'monospace' }}
                >
                  {getOutputSchemaPreview(ruleCategories, [skillId])}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <SkillWorkflowExplainer skillId={skillId} ruleCategories={ruleCategories} />
        </Grid>
      </Grid>
    </Box>
  );
};

/** Tag names for document-composer templates. Resolved in BLL; not stored in aiPromptTags. */
const DOCUMENT_COMPOSER_RUNTIME_ONLY_TAG_NAMES = [
  '@asset.grammar',
  '@asset.userResponseContent',
  '@asset.outputContent',
  '@skill.requirements.rules',
  '@skill.requirements.explainer',
  '@skill.redactions.rules',
  '@skill.redactions.explainer',
  '@skill.validation.rules',
  '@skill.validation.explainer',
] as const;

/** Renders Mermaid flowchart syntax as SVG. Uses neutral theme for admin UI. */
const WorkflowMermaidDiagram = ({ chart }: { chart: string }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const renderIdRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const currentRenderId = ++renderIdRef.current;
    const uniqueId = `workflow-mermaid-${currentRenderId}-${Date.now()}`;

    const run = async () => {
      setError(null);
      setSvgContent(null);
      try {
        mermaid.initialize({
          startOnLoad: false,
          theme: 'dark',
          securityLevel: 'loose',
          themeVariables: {
            primaryTextColor: '#fff',
            secondaryTextColor: '#fff',
            tertiaryTextColor: '#fff',
            primaryBorderColor: '#666',
            lineColor: '#888',
            secondaryBorderColor: '#666',
            tertiaryBorderColor: '#666',
            background: '#1e1e1e',
            mainBkg: '#2d2d2d',
            secondBkg: '#1e1e1e',
            tertiaryBkg: '#1e1e1e',
            textColor: '#fff',
            fontSize: '16px',
          },
        });
        const { svg } = await mermaid.render(uniqueId, chart);
        if (isMounted && currentRenderId === renderIdRef.current) {
          setSvgContent(svg);
        }
      } catch (err: any) {
        if (isMounted && currentRenderId === renderIdRef.current) {
          setError(err.message ?? 'Failed to render diagram');
        }
      }
    };
    run().catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [chart]);

  if (error) {
    return (
      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
        Diagram error: {error}
      </Typography>
    );
  }
  if (!svgContent) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Loading diagram…
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        minHeight: 200,
        bgcolor: 'grey.900',
        '& svg': {
          maxWidth: '100%',
          height: 'auto',
          minWidth: 380,
        },
        '& svg text': { fill: '#fff !important' },
        '& svg tspan': { fill: '#fff !important' },
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

const LabelWithHelp = ({ label }: { label: string; help: string }) => label;

const applicableAssetTypeChoices = [
  { id: 'therapistSessionNotes', name: 'Session Notes' },
];

const CreateToolbar = ({ onCancel, ...props }: any) => (
  <Toolbar
    {...props}
    sx={{
      backgroundColor: 'transparent',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 2,
      '& .RaToolbar-defaultToolbar': {
        backgroundColor: 'transparent',
      },
    }}
  >
    <Button onClick={onCancel} color="inherit">
      Cancel
    </Button>
    <SaveButton />
  </Toolbar>
);

const EditToolbar = (props: any) => (
  <Toolbar
    {...props}
    sx={{
      backgroundColor: 'transparent',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 2,
      '& .RaToolbar-defaultToolbar': {
        backgroundColor: 'transparent',
      },
    }}
  >
    <SaveButton />
  </Toolbar>
);

const DEFAULT_RULE_ITEM = (categoryId: string) => ({
  id: categoryId ? `${categoryId}_` : '',
  fieldType: 'text_area',
  description: '',
  agentPromptDescription: '',
});

type RuleItemForm = {
  id: string;
  fieldType: string;
  description: string;
  agentPromptDescription: string;
};

type RuleCategoryForm = {
  id: string;
  rules: RuleItemForm[];
};

/** Skill id filter for stepper steps (analysis, validation). */
type FilterSkillId = 'requirements' | 'redactions' | 'validation';

/** Allowed analysis skills: Requirements, Redactions, Validation. */
const DOCUMENT_COMPOSER_SKILL_CHOICES = [
  { id: 'requirements', name: 'Requirements' },
  { id: 'redactions', name: 'Redactions' },
  { id: 'validation', name: 'Validation' },
] as const;

/** Fixed skill config for preview only (must match backend skills). Used when building @skill.outputSchema preview. */
const ADMIN_SKILL_PREVIEW_CONFIG: Record<
  string,
  {
    statuses: { id: string; setBy: string; whenToUse?: string }[];
    fieldRules: { statusIds: string[]; field: string; behavior?: 'set' | 'clear'; description: string }[];
  }
> = {
  requirements: {
    statuses: [
      { id: 'requested', setBy: 'ai', whenToUse: 'Rule subject is missing or unclear; request it from the user.' },
      { id: 'provided', setBy: 'user', whenToUse: 'User has provided the requested content.' },
      { id: 'skipped', setBy: 'user', whenToUse: 'User chose to skip this rule.' },
    ],
    fieldRules: [],
  },
  redactions: {
    statuses: [
      { id: 'suggested', setBy: 'ai', whenToUse: 'Candidate removals identified.' },
      { id: 'skipped', setBy: 'user', whenToUse: 'User chose to not apply this redaction.' },
      { id: 'approved', setBy: 'user', whenToUse: 'User has approved this removal.' },
    ],
    fieldRules: [
      { statusIds: ['suggested'], field: 'excerpt', behavior: 'set', description: 'The exact excerpt or passage from the note that is a candidate for removal.' },
      { statusIds: ['suggested'], field: 'reason', behavior: 'set', description: 'Why this content should be removed or generalized.' },
      { statusIds: ['suggested'], field: 'suggestedOperation', behavior: 'set', description: 'Propose "remove" (delete) or "generalize" (replace with generalized text). When "generalize", also provide the suggestion field.' },
      { statusIds: ['suggested'], field: 'suggestion', behavior: 'set', description: 'When suggestedOperation is "generalize": the proposed replacement text (required). When "remove": null.' },
    ],
  },
  validation: {
    statuses: [
      { id: 'pass', setBy: 'ai', whenToUse: 'The document satisfies this validation rule.' },
      { id: 'fail', setBy: 'ai', whenToUse: 'The document does not satisfy this validation rule.' },
    ],
    fieldRules: [
      { statusIds: ['fail'], field: 'reason', behavior: 'set', description: 'Why the validation rule failed (what was violated or what changed).' },
    ],
  },
};

/** Mermaid sequence diagram: Requirements — User presents note + rules to AI. */
const REQUIREMENTS_WORKFLOW_CHART = `sequenceDiagram
  participant User
  participant AI
  User->>AI: original note + rules
  AI->>AI: evaluate
  alt content missing
    AI->>User: requested
    User->>AI: provide / skip
  else content in note
    AI->>User: requested (or satisfied)
  end`;

/** Mermaid sequence diagram: Redactions — User presents original note to AI. */
const REDACTIONS_WORKFLOW_CHART = `sequenceDiagram
  participant User
  participant AI
  User->>AI: original note
  AI->>AI: evaluate
  AI->>User: matches
  User->>AI: approve / skip`;

/** Mermaid sequence diagram: Validation — inputs already available; AI runs validation only (no user step). */
const VALIDATION_WORKFLOW_CHART = `sequenceDiagram
  participant User
  participant AI
  Note over User,AI: Inputs: original request,<br/>analysis result, user response,<br/>results data
  AI->>AI: evaluate output vs original
  AI->>User: pass / fail + reason`;

const DEFAULT_ANALYSIS_OUTPUT_EXPLAINER_CLIENT = [
  'Return one object per rule.',
  '',
  'Each object has:',
  '- **ruleId** (string): the rule id.',
  '- **status** (string): one of the category status ids (see generated section below).',
  '- Any additional fields described in the generated section below, when the status applies.',
].join('\n');

const DEFAULT_REDACTIONS_OUTPUT_EXPLAINER_CLIENT = [
  'Return one object per rule.',
  '',
  'Each object has:',
  '- **ruleId** (string): the rule id.',
  '- **matches** (array): zero or more match objects. Each match has:',
  '  - **matchId** (string): AI-generated kebab-case id for tracking (e.g. third-party-identifier-1).',
  '  - **start** (number): character start offset of the excerpt in the note.',
  '  - **end** (number): character end offset of the excerpt in the note.',
  '  - **status** (string): one of the category status ids (see generated section below).',
  '  - **excerpt** (string, required): the exact excerpt or passage from the note that is a candidate for removal.',
  '  - Any additional fields described in the generated section below.',
].join('\n');

const DEFAULT_VALIDATION_OUTPUT_EXPLAINER_CLIENT = [
  'Return one object per rule.',
  '',
  'Each object has:',
  '- **ruleId** (string): the rule id.',
  '- **status** (string): one of the skill status ids (pass or fail; see generated section below).',
  '- When status is fail, **reason** (string): why the validation rule failed.',
].join('\n');

const DEFAULT_CREATE_VALUES = {
  templates: {},
  ruleCategories: [
    { id: 'requirements', rules: [] },
    { id: 'redactions', rules: [] },
    { id: 'validation', rules: [] },
  ],
  applyTemplate: { systemMessage: '', userPromptTemplate: '' },
  assetType: 'therapistSessionNotes',
};

/** flattening logic used in transform */
const flattenRuleCategoriesToRules = (
  ruleCategories: RuleCategoryForm[],
): Array<{
  id: string;
  skill: string;
  fieldType: string;
  description: string;
  agentPromptDescription?: string;
}> => {
  return ruleCategories.flatMap((cat) => {
    return (cat.rules ?? []).map((r) => ({
      id: r.id,
      skill: cat.id,
      fieldType: r.fieldType ?? 'text_area',
      description: r.description ?? '',
      agentPromptDescription: r.agentPromptDescription ?? '',
    }));
  });
};

/** Build generated field section for preview using fixed ADMIN_SKILL_PREVIEW_CONFIG. */
const buildGeneratedFieldSectionForPreview = (
  ruleCategories: RuleCategoryForm[],
  skillIds: string[],
): string => {
  const skillIdSet = new Set(skillIds?.length ? skillIds : ruleCategories.map((c) => c.id));
  const parts: string[] = [];
  for (const cat of ruleCategories ?? []) {
    if (!skillIdSet.has(cat.id)) continue;
    const config = ADMIN_SKILL_PREVIEW_CONFIG[cat.id];
    if (!config?.statuses?.length) continue;
    const aiStatuses = config.statuses.filter((s) => (s.setBy ?? 'ai') === 'ai');
    if (aiStatuses.length === 0) continue;
    
    const isRedactions = cat.id === 'redactions';
    if (isRedactions) {
      parts.push('Each match has:');
      parts.push('- **matchId**: AI-generated kebab-case id for tracking (e.g. third-party-identifier-1).');
    } else {
      parts.push('Each object has:');
    }

    const statusIds = aiStatuses.map((s) => s.id).join(', ');
    parts.push(`- **status**: one of ${statusIds}.`);
    for (const s of aiStatuses) {
      if (s.whenToUse) parts.push(`  - **${s.id}**: ${s.whenToUse}`);
    }
    const aiStatusIdSet = new Set(aiStatuses.map((s) => s.id));
    const fieldRules = (config.fieldRules ?? []).filter((fr) => fr.statusIds.some((id) => aiStatusIdSet.has(id)));
    if (fieldRules.length > 0) {
      parts.push(isRedactions ? 'Additional fields per match (when status applies):' : 'Additional fields per object (when status applies):');
      for (const fr of fieldRules) {
        const statusIdsForRule = fr.statusIds.filter((id) => aiStatusIdSet.has(id)).join(', ');
        const behavior = fr.behavior ?? 'set';
        const desc =
          behavior === 'set'
            ? (fr.description ?? '').trim() || 'Provide as appropriate.'
            : 'Leave empty.';
        parts.push(`- When status is one of [${statusIdsForRule}], **${fr.field}**: ${desc}`);
      }
    }
  }
  return parts.length > 0 ? parts.join('\n') : '';
};

/** Preview for {{ @skill.[skillName].rules }}: plain list of rule id + configured rule behavior (no header). */
const getSkillRulesPreview = (
  ruleCategories: RuleCategoryForm[],
  skillIds: string[],
): string => {
  const skillIdSet = new Set(skillIds ?? []);
  const rulesInScope = (ruleCategories ?? [])
    .filter((cat: RuleCategoryForm) => skillIdSet.has(cat.id))
    .flatMap((cat: RuleCategoryForm) => cat.rules ?? []);
  return rulesInScope.length === 0
    ? 'No rules in the selected skill(s). Add rules or select skills that contain rules.'
    : rulesInScope
        .map(
          (r: RuleItemForm) =>
            `- **${r.id}**: ${r.agentPromptDescription ?? r.description ?? '(no description)'}`,
        )
        .join('\n');
};

/** Preview for {{ @skill.[skillName].explainer }}: expected statuses and fields for a single skill (matches backend Phase 1). */
const getExplainerPreview = (
  ruleCategories: RuleCategoryForm[],
  skillId: string,
): string => {
  const explainer =
    skillId === 'requirements'
      ? DEFAULT_ANALYSIS_OUTPUT_EXPLAINER_CLIENT
      : skillId === 'redactions'
        ? DEFAULT_REDACTIONS_OUTPUT_EXPLAINER_CLIENT
        : skillId === 'validation'
          ? DEFAULT_VALIDATION_OUTPUT_EXPLAINER_CLIENT
          : '';
  const generatedSection = buildGeneratedFieldSectionForPreview(ruleCategories, [skillId]);
  const parts = [explainer, generatedSection].filter(Boolean);
  return parts.join('\n\n');
};

/** Preview for {{ @skill.outputSchema }}: JSON schema for analyze output (array of objects). Requirements use rule-level status; Redactions use match-level status. */
const getOutputSchemaPreview = (
  ruleCategories: RuleCategoryForm[],
  skillIds: string[],
): string => {
  const ids = skillIds ?? [];
  if (ids.length === 0) return '(No skills selected.)';
  if (ids.length === 2 && ids.includes('requirements') && ids.includes('redactions')) {
    return JSON.stringify(
      {
        type: 'object',
        properties: {
          inclusion: JSON.parse(getOutputSchemaPreview(ruleCategories, ['requirements'])),
          redactions: JSON.parse(getOutputSchemaPreview(ruleCategories, ['redactions'])),
        },
        required: ['inclusion', 'redactions'],
      },
      null,
      2,
    );
  }
  const rules = (ruleCategories ?? [])
    .filter((cat: RuleCategoryForm) => ids.includes(cat.id))
    .flatMap((cat: RuleCategoryForm) => cat.rules ?? []);
  const ruleIds = rules.map((r) => r.id);
  const statusEnum: string[] = [];
  const extraProperties: Record<string, { type: string; description?: string }> = {};
  for (const skillId of ids) {
    const config = ADMIN_SKILL_PREVIEW_CONFIG[skillId];
    if (!config?.statuses?.length) continue;
    const aiStatuses = config.statuses.filter((s) => (s.setBy ?? 'ai') === 'ai');
    for (const s of aiStatuses) {
      if (!statusEnum.includes(s.id)) statusEnum.push(s.id);
    }
    const aiStatusIdSetForSkill = new Set(aiStatuses.map((s) => s.id));
    for (const fr of config.fieldRules ?? []) {
      if (fr.statusIds.some((id) => aiStatusIdSetForSkill.has(id)) && fr.field) {
        const statusLabel =
          fr.statusIds?.length > 0
            ? `Only generate if the status is one of [${fr.statusIds.map((id) => `"${id}"`).join(', ')}].`
            : '';
        const behavior = fr.behavior ?? 'set';
        const instruction =
          behavior === 'set'
            ? (fr.description ?? '').trim() || 'Provide as appropriate.'
            : 'Leave empty.';
        const desc = [instruction, statusLabel].filter(Boolean).join(' ') || undefined;
        extraProperties[fr.field] = { type: 'string', description: desc };
      }
    }
  }
  const matchItemSchema: Record<string, unknown> = {
    matchId: { type: 'string', description: 'AI-generated kebab-case id for tracking (e.g. third-party-identifier-1)' },
    start: { type: 'number', description: 'Character start offset of the excerpt in the note' },
    end: { type: 'number', description: 'Character end offset of the excerpt in the note' },
    status: { type: 'string', enum: statusEnum.length > 0 ? statusEnum : undefined },
    excerpt: { type: 'string', description: 'The exact excerpt or passage from the note that is a candidate for removal' },
    ...extraProperties,
  };
  const schema =
    ids.includes('requirements') || ids.includes('validation')
      ? {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              ruleId: { type: 'string', enum: ruleIds.length > 0 ? ruleIds : undefined },
              status: { type: 'string', enum: statusEnum.length > 0 ? statusEnum : undefined },
              ...extraProperties,
            },
            required: ['ruleId', 'status'],
          },
        }
      : {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              ruleId: { type: 'string', enum: ruleIds.length > 0 ? ruleIds : undefined },
              matches: { type: 'array', items: { type: 'object', properties: matchItemSchema, required: ['matchId', 'status'] } },
            },
            required: ['ruleId', 'matches'],
          },
        };
  return JSON.stringify(schema, null, 2,);
};

/** Preview for {{ @asset.userResponseContent }}: one markdown table per skill. Requirements: rule-level (Rule ID | Status | User response). Redactions: match-level (Match ID | Rule ID | Status | User response). */
const getUserResponseContentExamplePreview = (
  ruleCategories: RuleCategoryForm[],
  skillIds: string[],
): string => {
  const skillIdSet = new Set(skillIds ?? []);
  const categoriesInScope = (ruleCategories ?? []).filter((cat: RuleCategoryForm) =>
    skillIdSet.has(cat.id),
  );
  if (categoriesInScope.length === 0) {
    return '(No rules in selected skills. Select skills to see an example.)';
  }
  const escapeCell = (s: string) => s.replace(/\n/g, ' ').replace(/\|/g, '\\|');
  const sections: string[] = [];
  const sectionTitle = (id: string) => (id === 'requirements' ? 'Requirements' : id === 'redactions' ? 'Redactions' : id);
  for (const cat of categoriesInScope) {
    const rules = (cat.rules ?? []) as RuleItemForm[];
    if (rules.length === 0) {
      sections.push(`## ${sectionTitle(cat.id)}\n\n(No rules in this category.)`);
      continue;
    }
    const isRedactions = cat.id === 'redactions';
    if (isRedactions) {
      const rows: string[] = [];
      let matchNumber = 0;
      for (const r of rules) {
        const numMatches = 2;
        for (let j = 0; j < numMatches; j++) {
          matchNumber++;
          const status = j === 0 ? 'approved' : 'skipped';
          const suggestedAction = j === 0 ? 'generalize' : 'remove';
          const suggestedContent = suggestedAction === 'generalize' ? `Generalized content for ${r.id}` : '';
          rows.push(`| ${escapeCell(r.id)} | ${matchNumber} | ${escapeCell(suggestedAction)} | ${escapeCell(suggestedContent)} | ${escapeCell(status)} |`);
        }
      }
      const table = [
        '| Rule ID | Match ID | Suggested action | Suggested content | This status |',
        '| --- | --- | --- | --- | --- |',
        ...rows,
      ].join('\n');
      sections.push(`## ${sectionTitle(cat.id)}\n\n${table}`);
    } else {
      const rows = rules.map((r, i) => {
        const status = i % 2 === 0 ? 'provided' : 'skipped';
        const response = status === 'provided' ? `Example response for ${r.id}` : '';
        return `| ${escapeCell(r.id)} | ${escapeCell(status)} | ${escapeCell(response)} |`;
      });
      const table = ['| Rule ID | Status | User response |', '| --- | --- | --- |', ...rows].join('\n');
      sections.push(`## ${sectionTitle(cat.id)}\n\n${table}`);
    }
  }
  return sections.join('\n\n');
};

/** Inline skill ID field for the collapsible header: Select with skill choices (requirements, redactions, documentCreation, validation). Disabled when the skill has any rules or workflow status. */
/** Read-only explainer for Requirements skill: fixed statuses and fields. */
const RequirementsSkillExplainer = () => (
  <Box sx={{ py: 1 }}>
    <Typography variant="body2" paragraph sx={{ mt: 0 }}>
      <strong>Requirements</strong> ensures required content is present. The AI returns one object per rule with <strong>ruleId</strong>, <strong>status</strong>, and optional fields. Statuses are fixed in code.
    </Typography>
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Statuses (set by AI unless noted)</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      <li><strong>requested</strong> — Rule subject is missing or unclear; request it from the user (use the rule&apos;s user-facing description as the prompt).</li>
      <li><strong>provided</strong> (user) — User has provided the requested content.</li>
      <li><strong>skipped</strong> (user) — User chose to skip this rule.</li>
    </Box>
    <WorkflowMermaidDiagram chart={REQUIREMENTS_WORKFLOW_CHART} />
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
      When content is missing, the AI returns requested; the user then provides or skips. There is no second AI approval step.
    </Typography>
  </Box>
);

/** Read-only explainer for Redactions skill: fixed statuses and fields. */
const RedactionsSkillExplainer = () => (
  <Box sx={{ py: 1 }}>
    <Typography variant="body2" paragraph sx={{ mt: 0 }}>
      <strong>Redactions</strong> identifies content to remove or generalize. The AI returns one object per rule with <strong>ruleId</strong> and <strong>matches</strong> (each match has a unique <strong>matchId</strong>, <strong>start</strong>, <strong>end</strong>, <strong>excerpt</strong> (required), and <strong>status</strong>). Statuses are fixed in code.
    </Typography>
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Statuses (set by AI unless noted)</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      <li>
        <strong>suggested</strong> — Candidate removals identified.
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5, mb: 1, maxWidth: 720 }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'divider', py: 0.75, px: 1.5 }, '& th': { fontWeight: 600 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><strong>start</strong>, <strong>end</strong> (required)</TableCell>
                <TableCell>Character start and end offsets of the excerpt in the note.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>excerpt</strong> (required)</TableCell>
                <TableCell>Exact excerpt or passage from the note that is a candidate for removal.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>reason</strong> (required)</TableCell>
                <TableCell>Why this content should be removed or generalized.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>suggestedOperation</strong> (required)</TableCell>
                <TableCell>Propose <em>remove</em> (delete the excerpt) or <em>generalize</em> (replace with a generalized version). When generalize, you must also provide <strong>suggestion</strong>.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>suggestion</strong></TableCell>
                <TableCell>When suggestedOperation is <em>generalize</em>: the proposed replacement text (required). When <em>remove</em>: null.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </li>
      <li><strong>skipped</strong> (user) — User chose to not apply this redaction.</li>
      <li><strong>approved</strong> (user) — User has approved this removal.</li>
    </Box>
    <WorkflowMermaidDiagram chart={REDACTIONS_WORKFLOW_CHART} />
  </Box>
);

/** Read-only explainer for Document Creation skill: writes the document, validates previous state; status generated or failed with reason. */
const DocumentCreationSkillExplainer = () => (
  <Box sx={{ py: 1 }}>
    <Typography variant="body2" paragraph sx={{ mt: 0 }}>
      <strong>Document creation</strong> writes the document. It validates that the previous state has all required information from the rule analysis; if not, it fails with a reason. The AI returns one object per rule with <strong>ruleId</strong>, <strong>status</strong> (generated or failed), <strong>outputContent</strong> when generated, and <strong>reason</strong> when failed.
    </Typography>
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Statuses (set by AI)</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      <li>
        <strong>generated</strong> — The document was written successfully. Provide <strong>outputContent</strong> (full document text for the outputContent field).
      </li>
      <li>
        <strong>failed</strong> — Document creation failed (e.g. not all required information is present). Provide <strong>reason</strong> (why it failed).
      </li>
    </Box>
  </Box>
);

/** Read-only explainer for Validation skill: fixed statuses and fields. */
const ValidationSkillExplainer = () => (
  <Box sx={{ py: 1 }}>
    <Typography variant="body2" paragraph sx={{ mt: 0 }}>
      <strong>Validation</strong> evaluates the output document against the original. The AI returns one object per rule with <strong>ruleId</strong>, <strong>status</strong> (pass or fail), and <strong>reason</strong> when status is fail. This step runs only when output content exists (after Apply). Statuses are fixed in code.
    </Typography>
    <Typography variant="body2" paragraph sx={{ mt: 1 }}>
      Validation uses four inputs that are already available at that point: <strong>original request</strong> (user data), <strong>analysis result</strong> (from previous analyze steps), <strong>user response</strong> (provided user data), and <strong>results data</strong> (output after Apply). Once all of that is in place, validation runs entirely in the AI — there is no step at which the user is involved for validation.
    </Typography>
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Statuses (set by AI)</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      <li>
        <strong>pass</strong> — The document satisfies this validation rule.
      </li>
      <li>
        <strong>fail</strong> — The document does not satisfy this validation rule.
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5, mb: 1, maxWidth: 720 }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'divider', py: 0.75, px: 1.5 }, '& th': { fontWeight: 600 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><strong>reason</strong></TableCell>
                <TableCell>Why the validation rule failed (what was violated or what changed).</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </li>
    </Box>
    <WorkflowMermaidDiagram chart={VALIDATION_WORKFLOW_CHART} />
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
      Validation is entirely AI-operated; the user is not involved in this step.
    </Typography>
  </Box>
);

/** Shared workflow explainer panel: optionally preview tag dropdown + injected content; always skill explainer (statuses + diagram). Used in RULES tab (explainer only) and Prompt Editor tab (full). */
const SkillWorkflowExplainer = ({
  skillId,
  ruleCategories,
  showReplacementTagsPreview = true,
}: {
  skillId: string;
  ruleCategories: RuleCategoryForm[];
  showReplacementTagsPreview?: boolean;
}) => {
  const defaultTag = `@skill.${skillId}.rules`;
  const [selectedPreviewTag, setSelectedPreviewTag] = useState(defaultTag);

  const ExplainerComponent = useMemo(() => {
    if (skillId === 'redactions') return RedactionsSkillExplainer;
    if (skillId === 'validation') return ValidationSkillExplainer;
    if (skillId === 'requirements') return RequirementsSkillExplainer;
    return null;
  }, [skillId]);

  const previewTagChoices = useMemo(() => {
    const promptTags: { id: string; name: string }[] = [
      { id: '@skill.requirements.rules', name: '@skill.requirements.rules (requirements rule list)' },
      { id: '@skill.requirements.explainer', name: '@skill.requirements.explainer (requirements statuses and fields)' },
      { id: '@skill.redactions.rules', name: '@skill.redactions.rules (redactions rule list)' },
      { id: '@skill.redactions.explainer', name: '@skill.redactions.explainer (redactions statuses and fields)' },
      { id: '@skill.validation.rules', name: '@skill.validation.rules (validation rule list)' },
      { id: '@skill.validation.explainer', name: '@skill.validation.explainer (validation statuses and fields)' },
    ];
    const assetTags: { id: string; name: string }[] = [
      { id: '@asset.grammar', name: '@asset.grammar (runtime)' },
      { id: '@asset.userResponseContent', name: '@asset.userResponseContent (runtime)' },
      ...(skillId === 'validation'
        ? [{ id: '@asset.outputContent', name: '@asset.outputContent (runtime)' }]
        : []),
    ];
    return [...promptTags, ...assetTags];
  }, [skillId]);

  const getPreviewContent = useCallback(
    (tag: string): string => {
      if (tag === '@skill.requirements.rules') return getSkillRulesPreview(ruleCategories, ['requirements']);
      if (tag === '@skill.requirements.explainer') return getExplainerPreview(ruleCategories, 'requirements');
      if (tag === '@skill.redactions.rules') return getSkillRulesPreview(ruleCategories, ['redactions']);
      if (tag === '@skill.redactions.explainer') return getExplainerPreview(ruleCategories, 'redactions');
      if (tag === '@skill.validation.rules') return getSkillRulesPreview(ruleCategories, ['validation']);
      if (tag === '@skill.validation.explainer') return getExplainerPreview(ruleCategories, 'validation');
      if (tag === '@asset.grammar') return '(Injected at runtime from the session note.)';
      if (tag === '@asset.userResponseContent') return getUserResponseContentExamplePreview(ruleCategories, [skillId]);
      if (tag === '@asset.outputContent') return '(Injected at runtime: the output document after Apply.)';
      return '';
    },
    [ruleCategories, skillId],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {showReplacementTagsPreview && (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel id={`workflow-preview-tag-label-${skillId}`}>Preview replacement tag</InputLabel>
            <Select
              labelId={`workflow-preview-tag-label-${skillId}`}
              value={selectedPreviewTag}
              label="Preview replacement tag"
              onChange={(e) => setSelectedPreviewTag(e.target.value)}
            >
              {previewTagChoices.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: 'action.hover',
              maxHeight: 400,
              overflow: 'auto',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {`{{ ${selectedPreviewTag} }}`} — content injected for this skill:
            </Typography>
            {selectedPreviewTag === '@asset.userResponseContent' ? (
              <Box sx={{ '& table': { fontSize: '0.8rem' }, '& td, & th': { px: 1, py: 0.5, border: '1px solid', borderColor: 'divider' } }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {getPreviewContent(selectedPreviewTag)}
                </ReactMarkdown>
              </Box>
            ) : (
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontSize: '0.8rem' }}
              >
                {getPreviewContent(selectedPreviewTag)}
              </Typography>
            )}
          </Paper>
        </>
      )}
      {ExplainerComponent && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <ExplainerComponent />
        </Paper>
      )}
    </Box>
  );
};

/** Rule ID input with fixed category prefix. User edits only the suffix; form stores full id = prefix + suffix. */
const PrefixedRuleIdInput = ({
  source,
  categoryId,
  label,
  help,
}: {
  source: string;
  categoryId: string;
  label: React.ReactNode;
  help: string;
}) => {
  const { control } = useFormContext();
  const prefix = categoryId ? `${categoryId}_` : '';
  return (
    <Controller
      name={source}
      control={control}
      rules={{ required: 'Rule ID is required' }}
      render={({ field, fieldState }) => {
        const fullId = field.value ?? '';
        const suffix =
          prefix === ''
            ? fullId
            : fullId.startsWith(prefix)
              ? fullId.slice(prefix.length)
              : fullId;
        return (
          <TextField
            fullWidth
            label={label}
            helperText={fieldState.error?.message ?? help}
            error={!!fieldState.error}
            value={suffix}
            onChange={(e) => field.onChange(prefix ? prefix + e.target.value : e.target.value)}
            onBlur={field.onBlur}
            InputProps={
              prefix
                ? {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ pointerEvents: 'none', mr: 0 }}>
                        <Typography component="span" variant="body1" color="text.secondary">
                          {prefix}
                        </Typography>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
        );
      }}
    />
  );
};

const CategoryRulesEditor = ({ categoryIndex }: { categoryIndex: number }) => {
  const { control } = useFormContext();
  const categoryId = useWatch({ name: `ruleCategories.${categoryIndex}.id`, defaultValue: '' });
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `ruleCategories.${categoryIndex}.rules`,
    keyName: 'ruleKey',
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<number | null>(null);

  const handleAddRule = () => {
    append(DEFAULT_RULE_ITEM(categoryId ?? ''));
  };

  const handleDeleteConfirm = (index: number) => {
    remove(index);
    setDeleteConfirmOpen(null);
  };

  return (
    <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider', mt: 1 }}>
      {fields.map((field, ruleIndex) => (
        <Box
          key={field.ruleKey}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            mb: 2,
            backgroundColor: 'action.hover',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                {ruleIndex + 1}
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PrefixedRuleIdInput
                  source={`ruleCategories.${categoryIndex}.rules.${ruleIndex}.id`}
                  categoryId={categoryId ?? ''}
                  label={
                    <LabelWithHelp
                      label="Rule ID"
                      help="Suffix only; category ID is automatically prefixed (e.g. service_info → inclusion_service_info)."
                    />
                  }
                  help="Suffix only; category ID is automatically prefixed."
                />
              </Grid>
              {categoryId !== 'validation' && (
                <Grid item xs={12}>
                  <TextInput
                    source={`ruleCategories.${categoryIndex}.rules.${ruleIndex}.description`}
                    label={
                      <LabelWithHelp
                        label="User-Facing Description"
                        help="Human-readable description shown to the user in the UI."
                      />
                    }
                    multiline
                    fullWidth
                    validate={[required()]}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextInput
                  source={`ruleCategories.${categoryIndex}.rules.${ruleIndex}.agentPromptDescription`}
                  label={
                    <LabelWithHelp
                      label="Agent Prompt Instructions"
                      help="Instructions for the AI to identify or process this rule. Appended to the system prompt."
                    />
                  }
                  multiline
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
            {fields.length > 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => move(ruleIndex, ruleIndex - 1)}
                  title="Move Up"
                  disabled={ruleIndex === 0}
                  sx={{ p: 1, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => move(ruleIndex, ruleIndex + 1)}
                  title="Move Down"
                  disabled={ruleIndex === fields.length - 1}
                  sx={{ p: 1, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <IconButton
              color="error"
              size="small"
              onClick={() => setDeleteConfirmOpen(ruleIndex)}
              title="Remove rule"
              sx={{ p: 1, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'error.light' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddRule}
        sx={{ mt: 1, mb: 2 }}
      >
        Add Rule
      </Button>
      <ConfirmDialog
        open={deleteConfirmOpen !== null}
        onClose={() => setDeleteConfirmOpen(null)}
        onConfirm={() =>
          deleteConfirmOpen !== null && handleDeleteConfirm(deleteConfirmOpen)
        }
        title="Delete Rule"
        content="Are you sure you want to delete this rule?"
        confirmLabel="Delete"
        titleId="delete-rule-dialog-title"
      />
    </Box>
  );
};

const RulesListEditor = ({ filterSkillIds }: { filterSkillIds?: FilterSkillId[] }) => {
  const { control } = useFormContext();
  const { fields } = useFieldArray({
    control,
    name: 'ruleCategories',
    keyName: 'catKey',
  });
  const [expandedIndices, setExpandedIndices] = useState<Record<number, boolean>>({});
  const [tabByCategory, setTabByCategory] = useState<Record<number, number>>({});

  const toggleExpanded = (index: number) => {
    setExpandedIndices((prev) => ({
      ...prev,
      [index]: prev[index] === false, // default to true if undefined
    }));
  };

  const isExpanded = (index: number) => expandedIndices[index] !== false;

  const ruleCategories = useWatch({ name: 'ruleCategories', defaultValue: [] }) ?? [];

  return (
    <Box sx={{ width: '100%' }}>
      {fields.map((field, index) => {
        const category = ruleCategories[index];
        if (filterSkillIds?.length && (!category?.id || !filterSkillIds.includes(category.id as FilterSkillId))) {
          return null;
        }
        const rulesCount = Array.isArray(category?.rules) ? category.rules.length : 0;
        const expanded = isExpanded(index);
        const skillName = DOCUMENT_COMPOSER_SKILL_CHOICES.find(c => c.id === category?.id)?.name ?? category?.id;

        return (
          <Box
            key={field.catKey}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mb: 3,
              backgroundColor: 'action.hover',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: 40,
                py: 1,
                px: 1.5,
                gap: 2,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.selected' },
              }}
              onClick={() => toggleExpanded(index)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                >
                  {index + 1}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {skillName}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                <Chip 
                  label={`${rulesCount} rules`} 
                  size="small" 
                  variant="outlined" 
                  sx={{ height: 20, fontSize: '0.7rem', opacity: 0.8 }} 
                />
              </Box>
            </Box>

            <Collapse in={expanded}>
              <Box sx={{ p: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Tabs
                  value={tabByCategory[index] ?? 0}
                  onChange={(_, v: number) => setTabByCategory((prev) => ({ ...prev, [index]: v }))}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab label="Rules" id={`category-${index}-tab-0`} aria-controls={`category-${index}-panel-0`} />
                  <Tab label="Prompt Editor" id={`category-${index}-tab-1`} aria-controls={`category-${index}-panel-1`} />
                </Tabs>
                
                <Box role="tabpanel" id={`category-${index}-panel-0`} aria-labelledby={`category-${index}-tab-0`} hidden={(tabByCategory[index] ?? 0) !== 0}>
                  {(tabByCategory[index] ?? 0) === 0 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={7}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>Rules</Typography>
                        <CategoryRulesEditor categoryIndex={index} />
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                            Output JSON Schema (passed to the agent directly)
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              bgcolor: 'action.hover',
                              maxHeight: 300,
                              overflow: 'auto',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              component="pre"
                              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontSize: '0.8rem', fontFamily: 'monospace' }}
                            >
                              {getOutputSchemaPreview(ruleCategories, [category?.id ?? ''])}
                            </Typography>
                          </Paper>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <SkillWorkflowExplainer
                          skillId={category?.id ?? ''}
                          ruleCategories={ruleCategories}
                          showReplacementTagsPreview={false}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>

                <Box role="tabpanel" id={`category-${index}-panel-1`} aria-labelledby={`category-${index}-tab-1`} hidden={(tabByCategory[index] ?? 0) !== 1}>
                  {(tabByCategory[index] ?? 0) === 1 && (
                      <SkillPromptEditor
                        skillId={category?.id || ''}
                        validTags={DOCUMENT_COMPOSER_RUNTIME_ONLY_TAG_NAMES as unknown as string[]}
                        ruleCategories={ruleCategories}
                      />
                  )}
                </Box>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

const transformTemplates = (data: any) => {
  const applyTemplate = data.applyTemplate ?? {};
  const templates = { ...(data.templates ?? {}) };

  templates.documentCreation = {
    systemMessage: applyTemplate.systemMessage ?? '',
    userPromptTemplate: applyTemplate.userPromptTemplate ?? '',
  };

  const rest = { ...data };
  delete rest.applyTemplate;
  return { ...rest, templates };
};

const transformRuleCategoriesToRules = (data: any) => {
  const ruleCategories: RuleCategoryForm[] = data.ruleCategories ?? [];
  const rules = flattenRuleCategoriesToRules(ruleCategories);
  const rest = { ...data };
  delete rest.ruleCategories;
  delete rest.promptEntries; // UI-only; derived from templates/operationSequence, do not send to backend
  return { ...rest, rules };
};

export const RulesetEdit = () => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      const withTemplates = transformTemplates(data);
      const withRules = transformRuleCategoriesToRules(withTemplates);
      return {
        ...withRules,
        lastUpdatedAt: new Date(),
        lastUpdatedBy: userId,
      };
    },
    [userId],
  );

  return (
    <Edit
      title={<RulesetTitle />}
      mutationMode="pessimistic"
      transform={transform}
      queryOptions={queryOptions}
    >
      <RulesetForm toolbar={<EditToolbar />} />
    </Edit>
  );
};

export const RulesetCreateDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const userId = useGetIdentity().data?.id?.toString();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isDirtyRef = useRef(false);

  const handleRequestClose = useCallback(() => {
    if (isDirtyRef.current) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [onClose]);

  const transform = useCallback(
    (data: any) => {
      const withTemplates = transformTemplates(data);
      const withRules = transformRuleCategoriesToRules(withTemplates);
      return {
        ...withRules,
        assetType: withRules.assetType || 'therapistSessionNotes',
        createdAt: new Date(),
        createdBy: userId,
      };
    },
    [userId],
  );

  const handleSuccess = useCallback(() => {
    notify('Ruleset created successfully', { type: 'info' });
    refresh();
    onClose();
  }, [notify, refresh, onClose]);

  return (
    <>
      <Dialog open={open} onClose={handleRequestClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Ruleset</DialogTitle>
        <DialogContent>
          <Create
            resource="documentComposerRulesets"
            component={Box}
            actions={false}
            transform={transform}
            mutationOptions={{ onSuccess: handleSuccess }}
            redirect={false}
          >
            <RulesetForm
              toolbar={<CreateToolbar onCancel={handleRequestClose} />}
              isDirtyRef={isDirtyRef}
            />
          </Create>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        onConfirm={onClose}
        title="Discard changes?"
        content="You have unsaved changes. Are you sure you want to close this dialog and lose your progress?"
        confirmLabel="Close"
        titleId="discard-dialog-title"
      />
    </>
  );
};

const RulesetTitle = () => {
  const record = useRecordContext();
  return <span>Ruleset: {record ? `${record.name}` : ''}</span>;
};

const DocumentComposerHowItWorksActions = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        startIcon={<HelpOutlineIcon />}
        onClick={() => setOpen(true)}
        size="small"
      >
        How this works
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle>Document Composer: How It Works</DialogTitle>
        <DialogContent dividers>
          <Typography component="div" sx={{ '& p': { mb: 1.5 }, '& ul': { pl: 2.5 } }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {DOCUMENT_COMPOSER_HOW_IT_WORKS_MD.trim()}
            </ReactMarkdown>
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DirtyStateTracker = ({ isDirtyRef }: { isDirtyRef?: React.MutableRefObject<boolean> }) => {
  const {
    formState: { isDirty },
  } = useFormContext();
  useEffect(() => {
    if (isDirtyRef) {
      isDirtyRef.current = isDirty;
    }
  }, [isDirty, isDirtyRef]);
  return null;
};

const FrameworkSelect = (props: any) => {
  const { data, isLoading } = useGetList('therapistSessionNotesFrameworks', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const choices = [
    { id: 'general', name: 'General (Apply when no specific framework matched)' },
    ...(data?.map(f => ({ id: f.id, name: f.name })) || []),
  ];

  return (
    <SelectInput
      source="assetSubtype"
      label={
        <LabelWithHelp
          label="Framework / Subtype"
          help="The specific framework or subtype of the asset this ruleset targets. Use 'General' for a fallback ruleset."
        />
      }
      choices={choices}
      isLoading={isLoading}
      validate={[required()]}
      fullWidth
      {...props}
    />
  );
};

/** Form content that must run inside SimpleForm so useFormContext/useWatch see the form. */
const RulesetFormContent = ({ isDirtyRef }: { isDirtyRef?: React.MutableRefObject<boolean> }) => {
  const record = useRecordContext();
  const docId = record?.id ?? record?._id;
  const ruleCategories = useWatch({ name: 'ruleCategories', defaultValue: [] }) ?? [];
  const [activeStep, setActiveStep] = useState(0);
  const [selectedApplyPreviewTag, setSelectedApplyPreviewTag] = useState('@asset.grammar');

  const validTags = DOCUMENT_COMPOSER_RUNTIME_ONLY_TAG_NAMES as unknown as string[];

  /** Document creation uses all rules from requirements + redactions (same tag set as analysis; output shape = both skills). */
  const applySkillIds = useMemo(
    () =>
      ruleCategories
        .filter((c: RuleCategoryForm) => c.id === 'requirements' || c.id === 'redactions')
        .map((c: RuleCategoryForm) => c.id),
    [ruleCategories],
  );

  const applyPreviewTagChoices = useMemo(() => {
    const promptTags: { id: string; name: string }[] = [
      { id: '@skill.requirements.rules', name: '@skill.requirements.rules (requirements rule list)' },
      { id: '@skill.requirements.explainer', name: '@skill.requirements.explainer (requirements statuses and fields)' },
      { id: '@skill.redactions.rules', name: '@skill.redactions.rules (redactions rule list)' },
      { id: '@skill.redactions.explainer', name: '@skill.redactions.explainer (redactions statuses and fields)' },
      { id: '@skill.validation.rules', name: '@skill.validation.rules (validation rule list)' },
      { id: '@skill.validation.explainer', name: '@skill.validation.explainer (validation statuses and fields)' },
    ];
    const assetTags: { id: string; name: string }[] = [
      { id: '@asset.grammar', name: '@asset.grammar (runtime)' },
      { id: '@asset.userResponseContent', name: '@asset.userResponseContent (runtime)' },
    ];
    return [...promptTags, ...assetTags];
  }, []);

  const getApplyPreviewContent = useCallback(
    (tag: string): string => {
      if (tag === '@skill.requirements.rules') return getSkillRulesPreview(ruleCategories, ['requirements']);
      if (tag === '@skill.requirements.explainer') return getExplainerPreview(ruleCategories, 'requirements');
      if (tag === '@skill.redactions.rules') return getSkillRulesPreview(ruleCategories, ['redactions']);
      if (tag === '@skill.redactions.explainer') return getExplainerPreview(ruleCategories, 'redactions');
      if (tag === '@skill.validation.rules') return getSkillRulesPreview(ruleCategories, ['validation']);
      if (tag === '@skill.validation.explainer') return getExplainerPreview(ruleCategories, 'validation');
      if (tag === '@asset.grammar') {
        return '(Injected at runtime from the session note.)';
      }
      if (tag === '@asset.userResponseContent') {
        return getUserResponseContentExamplePreview(ruleCategories, applySkillIds);
      }
      return '';
    },
    [ruleCategories, applySkillIds],
  );

  const steps = [
    { label: 'Analysis', description: 'Rules and prompts for requirements and redactions' },
    { label: 'Document creation', description: 'Workflow explainer and apply template (writes the document)' },
    { label: 'Validation', description: 'Rules and prompts for validation' },
  ];

  return (
    <>
      <DirtyStateTracker isDirtyRef={isDirtyRef} />
      {!!docId && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            mb: 2,
          }}
        >
          <Labeled label="ID">
            <Chip label={docId.toString()} />
          </Labeled>
          <DocumentComposerHowItWorksActions />
        </Box>
      )}

      <Paper sx={{ p: 3, mb: 3, width: '100%' }} elevation={1}>
        <Typography variant="h6" gutterBottom>
          General Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              source="name"
              label={<LabelWithHelp label="Name" help="Administrative name for this ruleset." />}
              validate={[required()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source="description"
              label={
                <LabelWithHelp
                  label="Description"
                  help="Internal description of the ruleset's purpose and what it targets."
                />
              }
              multiline
              fullWidth
            />
          </Grid>
        </Grid>

        <FormDataConsumer>
          {({ formData, ...rest }) => {
            const assetType = formData?.assetType || 'therapistSessionNotes';
            return (
              <Box sx={{ mt: 2 }}>
                {/* Only show assetType if it's not the default, or if we are creating and want to allow selection */}
                {assetType !== 'therapistSessionNotes' && (
                  <SelectInput
                    source="assetType"
                    label={
                      <LabelWithHelp
                        label="Asset Type"
                        help="The collection this ruleset applies to (e.g., Session Notes)."
                      />
                    }
                    choices={applicableAssetTypeChoices}
                    validate={[required()]}
                    {...rest}
                  />
                )}

                {assetType === 'therapistSessionNotes' && (
                  <FrameworkSelect {...rest} />
                )}
              </Box>
            );
          }}
        </FormDataConsumer>
      </Paper>

      <Stepper activeStep={activeStep} orientation="horizontal" nonLinear sx={{ width: '100%', mt: 2 }}>
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepButton onClick={() => setActiveStep(index)}>
              <StepLabel
                optional={index < 2 ? <Typography variant="caption">{step.description}</Typography> : null}
              >
                {step.label}
              </StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ mt: 3, mb: 2 }}>
        {activeStep === 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define what the AI checks and requests (requirements, redactions) and the prompts for each skill.
            </Typography>
            <RulesListEditor filterSkillIds={['requirements', 'redactions']} />
          </Box>
        )}
        {activeStep === 1 && (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TagInput
                    source="applyTemplate.systemMessage"
                    label={
                      <LabelWithHelp
                        label="System Message (Apply)"
                        help="System instructions for the document creation phase."
                      />
                    }
                    placeholder="Type system prompt for apply..."
                    validTags={validTags}
                    markup="{{__id__}}"
                  />
                  <TagInput
                    source="applyTemplate.userPromptTemplate"
                    label={
                      <LabelWithHelp
                        label="User Prompt Template (Apply)"
                        help="The template for the user message in the apply phase."
                      />
                    }
                    placeholder="Type user prompt for apply..."
                    validTags={validTags}
                    markup="{{__id__}}"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="apply-preview-tag-label">Preview replacement tag</InputLabel>
                    <Select
                      labelId="apply-preview-tag-label"
                      value={selectedApplyPreviewTag}
                      label="Preview replacement tag"
                      onChange={e => setSelectedApplyPreviewTag(e.target.value)}
                    >
                      {applyPreviewTagChoices.map(choice => (
                        <MenuItem key={choice.id} value={choice.id}>
                          {choice.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: 'action.hover',
                      maxHeight: 400,
                      overflow: 'auto',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {`{{ ${selectedApplyPreviewTag} }}`} — content injected when this tag is used in your apply template:
                    </Typography>
                    {(selectedApplyPreviewTag === '@asset.userResponseContent' || selectedApplyPreviewTag.startsWith('@skill.requirements.') || selectedApplyPreviewTag.startsWith('@skill.redactions.') || selectedApplyPreviewTag.startsWith('@skill.validation.')) ? (
                      <Box sx={{ '& table': { fontSize: '0.8rem' }, '& td, & th': { px: 1, py: 0.5, border: '1px solid', borderColor: 'divider' } }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {getApplyPreviewContent(selectedApplyPreviewTag)}
                        </ReactMarkdown>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, fontSize: '0.8rem' }}
                      >
                        {getApplyPreviewContent(selectedApplyPreviewTag)}
                      </Typography>
                    )}
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <DocumentCreationSkillExplainer />
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        {activeStep === 2 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define validation rules and the validation prompt. Validation runs entirely in the AI after document creation.
            </Typography>
            <RulesListEditor filterSkillIds={['validation']} />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            disabled={activeStep === 0}
            onClick={() => setActiveStep((s: number) => s - 1)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => setActiveStep((s: number) => Math.min(s + 1, steps.length - 1))}
          >
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export const RulesetForm = ({ toolbar, isDirtyRef }: any) => {
  const record = useRecordContext();
  const docId = record?.id ?? record?._id;
  const isCreate = !docId;

  return (
    <SimpleForm toolbar={toolbar} defaultValues={isCreate ? DEFAULT_CREATE_VALUES : undefined}>
      <RulesetFormContent isDirtyRef={isDirtyRef} />
    </SimpleForm>
  );
};
